import { createContext, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DEFAULT_CONFIG, DEFAULT_HOST } from '../const';
import ShopService from '../services/ShopService';

export const ShopContext = createContext();

export function ShopProvider({ children }) {
  const history = useHistory();
  const location = useLocation();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const firstSegmentNum = Number(pathSegments[0]);
  const hasShopIdInUrl = Boolean(pathSegments[0] && !isNaN(firstSegmentNum) && firstSegmentNum > 0);

  const shopIdFromUrl = hasShopIdInUrl ? firstSegmentNum : null;

  let remainingPath = '';
  if (hasShopIdInUrl) {
    remainingPath = pathSegments.length > 1 ? `/${pathSegments.slice(1).join('/')}` : '';
  } else {
    remainingPath = location.pathname === '/' ? '' : location.pathname;
  }

  const shopIdFromWindow = window?.sanoft?.shopId;

  const [shop, setShop] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_CONFIG.theme);
  const [loading, setLoading] = useState(false);

  const fetchProfile = useCallback(async (shopId) => {
    try {
      const response = await ShopService.getShopProfile(shopId);
      return response?.data;
    } catch (error) {
      console.error('Failed to fetch shop profile:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initShop = async () => {
      setLoading(true);

      let shopId = shopIdFromWindow || shopIdFromUrl;

      // If no shopId, lookup by hostname (skip for the main portal domain)
      const currentHost = window.location.host?.split(':')[0];
      if (!shopId && currentHost !== DEFAULT_HOST) {
        const lookupResult = ShopService.lookupShopByHost(window.location.host);
        if (lookupResult && lookupResult.shopId) {
          shopId = lookupResult.shopId;
        }
      }

      if (!shopId) {
        console.warn('Shop ID is missing. Using default config.');
        setLoading(false);
        return;
      }

      // Sync URL with shopId if it came from host lookup
      if (shopId !== shopIdFromUrl) {
        history.replace(`/${shopId}${remainingPath}`);
      }

      // Always fetch profile from API
      try {
        const profile = await fetchProfile(shopId);
        if (profile) {
          const shopData = { ...profile, shopId: profile.id };
          setTheme(profile.theme ?? DEFAULT_CONFIG.theme);
          if (isMounted) {
            setShop(shopData);
          }
        } else {
          console.warn('Shop profile not found for ID:', shopId);
        }
      } catch (error) {
        console.error('Failed to initialize shop profile:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initShop();

    return () => {
      isMounted = false;
    };
  }, [
    shopIdFromUrl,
    shopIdFromWindow,
    history,
    remainingPath,
    fetchProfile
  ]);

  return (
    <ShopContext.Provider value={{ shop, setShop, theme, loading }}>
      {children}
    </ShopContext.Provider>
  );
}

